import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Field, getFormValues, reduxForm } from 'redux-form';
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { toast } from 'react-toastify';
import renderSelectField from '../../shared/components/form/Select';
import renderCheckBoxField from '../../shared/components/form/CheckBox';
import FormBox from '../../shared/components/custom/FormBox';
import { partial } from '../../util/translation/translation';
import Api from '../../util/api';
import useConfig from '../../util/useConfig';
import Loading from '../../shared/components/custom/Loading';
import MatchCenterHelpers from './helpers';

// Every upcoming (non-concluded) match for the club, fetched once per
// modal open - large enough that pagination never matters for this list.
const FETCH_SIZE = 500;

const SCOPE_OPTIONS_KEY = ['all', 'home', 'away'];

// The value field is only editable once its "apply" checkbox is on - while
// it's off, disabling the control makes clear that field is left untouched
// rather than implying it'll be reset to whatever it currently shows.
const applyRow = (applyFieldName, label, valueField, applied) => (
  <Row className="my-2 flex-Vcenter">
    <Col md={1}>
      <Field
        name={applyFieldName}
        disableDefault
        component={renderCheckBoxField}
        type="checkbox"
      />
    </Col>
    <Col md={3}>
      <span className="form-header">{label}</span>
    </Col>
    <Col md={8}>{React.cloneElement(valueField, { disabled: !applied })}</Col>
  </Row>
);

const BulkEditLeagueModal = ({
  isOpen,
  toggle,
  companies,
  onDone,
  initialize,
  handleSubmit,
  formValues,
}) => {
  const config = useConfig();
  const mc = partial('matchCenter');
  const mf = partial('matchCenter.form');
  const s = partial('shared');

  const [loading, setLoading] = useState(false);
  const [allMatches, setAllMatches] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    initialize({
      league: null,
      scope: { value: 'all', label: mc('allGames') },
      applyVisibleInBanner: false,
      visibleInBanner: true,
      applyGamecenter: false,
      gamecenter: true,
      applyLineup: false,
      lineup: true,
      applyMvp: false,
      mvp: true,
      applyPlaypredict: false,
      playpredict: false,
      applySponsor: false,
      sponsor_name: null,
      applyPrSponsor: false,
      company: null,
    });
    setLoading(true);
    Api.matchCenter
      .getUpcomingMatches(
        1,
        FETCH_SIZE,
        config.teams.map((team) => team.id),
        false
      )
      .then((res) => setAllMatches(res?.data?.matches || []))
      .catch(() => toast.error(s('somethingWentWrong')))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const leagueOptions = useMemo(() => {
    const names = [
      ...new Set(
        allMatches.map((match) => match?.league?.name).filter(Boolean)
      ),
    ];
    return names.map((name) => ({ label: name, value: name }));
  }, [allMatches]);

  const scopeOptions = SCOPE_OPTIONS_KEY.map((value) => ({
    value,
    label: mc(
      { all: 'allGames', home: 'homeGamesOnly', away: 'awayGamesOnly' }[value]
    ),
  }));

  const isHomeGame = (match) =>
    !match.isCustom &&
    config.teams.some((team) => team.id === match.homeTeam?.id);
  const isAwayGame = (match) =>
    !match.isCustom &&
    config.teams.some((team) => team.id === match.awayTeam?.id);

  const matchesInScope = useMemo(() => {
    const league = formValues?.league?.value;
    if (!league) return [];
    const scope = formValues?.scope?.value || 'all';
    return allMatches
      .filter((match) => match?.league?.name === league)
      .filter((match) => {
        if (scope === 'home') return isHomeGame(match);
        if (scope === 'away') return isAwayGame(match);
        return true;
      });
  }, [allMatches, formValues?.league, formValues?.scope]);

  const handleClose = () => {
    toggle();
  };

  const onSubmit = async (values) => {
    const overrides = {};
    if (values.applyVisibleInBanner)
      overrides.visibleInBanner = values.visibleInBanner;
    if (values.applyGamecenter) overrides.gamecenter = values.gamecenter;
    if (values.applyLineup) overrides.lineup = values.lineup;
    if (values.applyMvp) overrides.mvp = values.mvp;
    if (values.applyPlaypredict) overrides.playpredict = values.playpredict;
    if (values.applySponsor && values.sponsor_name) {
      overrides.sponsorCompany =
        companies.find((company) => company.id === values.sponsor_name.value) ||
        null;
    }
    if (values.applyPrSponsor && values.company) {
      overrides.prSponsorCompanyId = values.company.value;
    }

    setLoading(true);
    const results = await Promise.allSettled(
      matchesInScope.map((match) =>
        Api.matchCenter.updateUpcomingMatch(
          config.id,
          match.id,
          MatchCenterHelpers.buildSettingsPayload(
            match,
            companies,
            config.teams,
            overrides
          )
        )
      )
    );
    setLoading(false);
    const successCount = results.filter(
      (result) => result.status === 'fulfilled'
    ).length;
    const message = mc('bulkEditDone', {
      success: successCount,
      total: results.length,
    });
    if (successCount === results.length) {
      toast.success(message);
    } else {
      toast.error(message);
    }
    onDone();
    handleClose();
  };

  const league = formValues?.league;
  const anyFieldSelected =
    formValues?.applyVisibleInBanner ||
    formValues?.applyGamecenter ||
    formValues?.applyLineup ||
    formValues?.applyMvp ||
    formValues?.applyPlaypredict ||
    formValues?.applySponsor ||
    formValues?.applyPrSponsor;
  const canApply = league && anyFieldSelected && !loading;

  const sponsorOptions = (companies || []).map((company) => ({
    label: company.name,
    value: company.id,
    disabled: company?.images?.appTicketImage === '',
  }));

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <Loading loading={loading} />
      <ModalHeader toggle={handleClose}>{mc('bulkEditLeague')}</ModalHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <Row>
            <Col md={6}>
              <div className="form__form-group">
                <span className="form-header">{mc('selectLeague')}</span>
                <Field
                  name="league"
                  placeholder={mc('selectLeague')}
                  options={leagueOptions}
                  component={renderSelectField}
                  isClearable
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="form__form-group">
                <span className="form-header">{mc('gameScope')}</span>
                <Field
                  name="scope"
                  options={scopeOptions}
                  component={renderSelectField}
                />
              </div>
            </Col>
          </Row>
          {league && (
            <>
              <p className="text-muted marginTop-5">
                {mc('bulkEditFieldsInfo')}
              </p>
              <FormBox style={{ marginBottom: 10 }} title={mf('bannerOptions')}>
                {applyRow(
                  'applyVisibleInBanner',
                  mc('bannerVisibility'),
                  <Field
                    name="visibleInBanner"
                    disableDefault
                    component={renderCheckBoxField}
                    type="checkbox"
                  />,
                  formValues?.applyVisibleInBanner
                )}
                {applyRow(
                  'applyGamecenter',
                  mf('gameCenter'),
                  <Field
                    name="gamecenter"
                    disableDefault
                    component={renderCheckBoxField}
                    type="checkbox"
                  />,
                  formValues?.applyGamecenter
                )}
                {applyRow(
                  'applyLineup',
                  mf('lineUp'),
                  <Field
                    name="lineup"
                    disableDefault
                    component={renderCheckBoxField}
                    type="checkbox"
                  />,
                  formValues?.applyLineup
                )}
                {applyRow(
                  'applyMvp',
                  mf('mvp'),
                  <Field
                    name="mvp"
                    disableDefault
                    component={renderCheckBoxField}
                    type="checkbox"
                  />,
                  formValues?.applyMvp
                )}
                {applyRow(
                  'applyPlaypredict',
                  mf('playPredict'),
                  <Field
                    name="playpredict"
                    disableDefault
                    component={renderCheckBoxField}
                    type="checkbox"
                  />,
                  formValues?.applyPlaypredict
                )}
              </FormBox>
              <FormBox style={{ marginBottom: 10 }} title={s('matchSponsor')}>
                {applyRow(
                  'applySponsor',
                  mf('matchSponsorName'),
                  <Field
                    name="sponsor_name"
                    placeholder={mf('matchSponsorName')}
                    options={sponsorOptions}
                    component={renderSelectField}
                  />,
                  formValues?.applySponsor
                )}
              </FormBox>
              <FormBox style={{ marginBottom: 10 }} title={mc('fanRating')}>
                {applyRow(
                  'applyPrSponsor',
                  s('sponsor'),
                  <Field
                    name="company"
                    placeholder={mf('playerRatingSponsor')}
                    options={sponsorOptions}
                    component={renderSelectField}
                  />,
                  formValues?.applyPrSponsor
                )}
              </FormBox>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button outline onClick={handleClose} type="button">
            {s('cancel')}
          </Button>
          <Button color="primary" disabled={!canApply} type="submit">
            {league
              ? mc('applyToMatches', { count: matchesInScope.length })
              : s('apply')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

const reduxF = reduxForm({ form: 'bulk_edit_league_form' });

const mapStateToProps = (state) => ({
  formValues: getFormValues('bulk_edit_league_form')(state),
});

export default connect(mapStateToProps)(reduxF(BulkEditLeagueModal));
